import { useRef, useState, useCallback, useEffect, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

interface PullToRefreshProps {
  children: ReactNode;
  enabled?: boolean;
}

const PULL_THRESHOLD = 70;
const MAX_PULL = 120;

export function PullToRefresh({ children, enabled = true }: PullToRefreshProps) {
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number>(0);
  const isPullingRef = useRef(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const isScrolledToTop = useCallback(() => {
    const el = containerRef.current;
    if (!el) return window.scrollY <= 0;
    return el.scrollTop <= 0 && window.scrollY <= 0;
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || isRefreshing) return;
    if (isScrolledToTop()) {
      startYRef.current = e.touches[0].clientY;
      isPullingRef.current = true;
    }
  }, [enabled, isRefreshing, isScrolledToTop]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isPullingRef.current || !enabled || isRefreshing) return;

    const diff = e.touches[0].clientY - startYRef.current;

    if (diff > 0 && isScrolledToTop()) {
      const dampened = Math.min(diff * 0.5, MAX_PULL);
      setPullDistance(dampened);
      if (dampened > 10) {
        e.preventDefault();
      }
    } else {
      if (diff <= 0) {
        isPullingRef.current = false;
        setPullDistance(0);
      }
    }
  }, [enabled, isRefreshing, isScrolledToTop]);

  const handleTouchEnd = useCallback(async () => {
    if (!isPullingRef.current || !enabled || isRefreshing) return;
    isPullingRef.current = false;

    if (pullDistance >= PULL_THRESHOLD) {
      setIsRefreshing(true);
      setPullDistance(PULL_THRESHOLD);

      try {
        await queryClient.invalidateQueries();
      } catch (err) {
        console.warn("Pull-to-refresh error:", err);
      }

      await new Promise(r => setTimeout(r, 500));
      setIsRefreshing(false);
    }

    setPullDistance(0);
  }, [pullDistance, enabled, isRefreshing, queryClient]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);
    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  useEffect(() => {
    if (!enabled && (pullDistance > 0 || isPullingRef.current)) {
      isPullingRef.current = false;
      setPullDistance(0);
    }
  }, [enabled, pullDistance]);

  const isPastThreshold = pullDistance >= PULL_THRESHOLD;
  const showIndicator = (pullDistance > 10 || isRefreshing) && enabled;
  const rotation = Math.min((pullDistance / PULL_THRESHOLD) * 360, 360);

  return (
    <div ref={containerRef} className="relative">
      {showIndicator && (
        <div
          className="fixed top-0 left-0 right-0 flex items-center justify-center pointer-events-none"
          style={{
            zIndex: 9999,
            height: isRefreshing ? PULL_THRESHOLD : pullDistance,
            transition: pullDistance === 0 ? "height 0.25s ease-out" : "none",
            overflow: "hidden",
          }}
        >
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full shadow-lg ${
              isPastThreshold || isRefreshing
                ? "bg-emerald-100 text-emerald-600"
                : "bg-white/90 text-gray-400"
            }`}
            style={{
              transition: "background-color 0.2s, color 0.2s",
              marginTop: "8px",
            }}
          >
            <RefreshCw
              className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`}
              style={
                !isRefreshing
                  ? { transform: `rotate(${rotation}deg)`, transition: "none" }
                  : undefined
              }
            />
          </div>
        </div>
      )}
      <div
        style={{
          transform: showIndicator ? `translateY(${isRefreshing ? PULL_THRESHOLD : pullDistance}px)` : "none",
          transition: pullDistance === 0 && !isRefreshing ? "transform 0.25s ease-out" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
